import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, Lightbulb } from 'lucide-react';

/**
 * TipBoxCarousel component for displaying multiple tips in a carousel
 * @param {string} pageId - Unique identifier for the page
 * @param {string} tipId - Unique identifier for the carousel
 * @param {Array} tips - Array of tip objects with { message, title, type, customData }
 * @param {string} position - Position: 'top', 'bottom' (default: 'top')
 * @param {boolean} dismissible - Whether the carousel can be dismissed (default: true)
 * @param {Function} onDismiss - Callback when carousel is dismissed
 * @param {boolean} centerInViewport - When true, visible tips render in a centered overlay (portal) with scroll
 * @param {boolean} showProTipCounter - When true, show "Pro Tip 1/N" above each title (default: true)
 * @param {string} showAgainLabel - Visible label when tips were dismissed (default: "Show Pro Tips")
 */
const TipBoxCarousel = ({
  pageId,
  tipId,
  tips = [],
  position = 'top',
  dismissible = true,
  onDismiss,
  centerInViewport = false,
  showProTipCounter = true,
  showAgainLabel = 'Show Pro Tips'
}) => {
  const storageKey = `tipbox_${pageId}_${tipId}`;
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    const dismissed = localStorage.getItem(storageKey);
    return dismissed !== 'true';
  });
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleDismiss = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(storageKey, 'true');
    }
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  }, [storageKey, onDismiss]);

  const handleNext = useCallback(() => {
    if (currentIndex < tips.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    } else {
      // On last tip, dismiss
      handleDismiss();
    }
  }, [currentIndex, tips.length, handleDismiss]);

  const handleClose = useCallback(() => {
    // X button advances to next tip instead of dismissing
    if (currentIndex < tips.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    } else {
      // On last tip, dismiss
      handleDismiss();
    }
  }, [currentIndex, tips.length, handleDismiss]);

  const handleGotIt = useCallback(() => {
    handleDismiss();
  }, [handleDismiss]);

  const goToTip = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const handleShowAgain = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(storageKey);
    }
    setIsVisible(true);
    setCurrentIndex(0);
  }, [storageKey]);

  if (!tips.length) return null;

  // Link to reopen tips after dismiss
  if (!isVisible) {
    return (
      <div className={`${position === 'top' ? 'mt-0 mb-3 sm:mb-4' : 'mb-4 mt-0'} mx-2 sm:mx-0 flex justify-end`}>
        <button
          onClick={handleShowAgain}
          className="text-sm text-left text-[#8B4513] hover:text-[#6B3410] underline transition-colors duration-200"
          aria-label={`Open tips: ${showAgainLabel}`}
        >
          {showAgainLabel}
        </button>
      </div>
    );
  }

  const currentTip = tips[currentIndex];
  if (!currentTip) return null;

  // Pro tip styling
  const config = {
    bg: 'bg-gradient-to-r from-yellow-50 to-amber-50',
    border: 'border-l-4 border-[#8B4513] border border-black',
    text: 'text-neutral-900',
    icon: Lightbulb,
    iconColor: 'text-[#8B4513]'
  };

  const Icon = config.icon;

  const outerMarginClasses = centerInViewport
    ? 'mt-0 mb-0 mx-0 w-full'
    : `
        ${position === 'top' ? 'mt-4 mb-3 sm:mb-4' : 'mb-4 mt-3 sm:mt-4'} 
        mx-2 sm:mx-0 
      `;

  const tipCard = (
      <div
      className={`
        ${config.bg} 
        ${outerMarginClasses}
        p-3 sm:p-4 
        rounded-xl 
        shadow-lg 
        relative 
        ${config.border}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Icon 
          className={`${config.iconColor} flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 mt-0.5`} 
          aria-hidden="true"
        />
        <div className="flex-grow pr-8 sm:pr-6">
          {showProTipCounter ? (
            <>
              <div className={`font-bold text-base sm:text-lg mb-2 ${config.text}`}>
                Pro Tip {currentIndex + 1}/{tips.length}
              </div>
              <div className="border-b border-black mb-3"></div>
              <div className={`font-semibold text-sm sm:text-base mb-2 ${config.text}`}>
                {currentTip.title || `Tip ${currentIndex + 1}`}
              </div>
            </>
          ) : (
            <>
              <div className={`font-bold text-lg sm:text-xl mb-2 ${config.text}`}>
                {currentTip.title || `Tip ${currentIndex + 1}`}
              </div>
              <div className="border-b border-black mb-3"></div>
            </>
          )}

          {/* Tip Message */}
          <div className={`${config.text} text-sm sm:text-base`}>
            {currentTip.message}
          </div>
        </div>
        {dismissible && (
          <button
            onClick={handleClose}
            className={`
              absolute top-2 right-2 
              ${config.text} 
              hover:opacity-70 
              transition-opacity 
              duration-200
              p-1
              rounded-lg
              hover:bg-white/50
              flex-shrink-0
            `}
            aria-label={currentIndex < tips.length - 1 ? "Next tip" : "Dismiss tip"}
          >
            <X size={18} className="sm:w-5 sm:h-5" />
          </button>
        )}
      </div>
      
      {/* Navigation controls at bottom */}
      {tips.length > 1 && (
        <div className="flex items-center justify-center relative mt-3 pt-3 min-h-[2.5rem]">
          {/* Dots indicator - centered */}
          <div className="flex items-center gap-2">
            {tips.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTip(index)}
                className={`
                  transition-all duration-200
                  w-2 h-2 rounded-full
                  ${index === currentIndex 
                    ? 'bg-[#8B4513]' 
                    : 'bg-gray-300 hover:bg-gray-400'
                  }
                `}
                aria-label={
                  tips[index]?.title
                    ? `Show tip: ${tips[index].title}`
                    : `Go to tip ${index + 1}`
                }
              />
            ))}
          </div>
          
          {/* Next tip / Got it button - positioned on the right */}
          <div className="absolute right-0 top-3">
            {currentIndex < tips.length - 1 ? (
              <button
                onClick={handleNext}
                className={`
                  px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg
                  text-[#8B4513]
                  hover:text-[#6B3410]
                  hover:bg-white/50
                  transition-colors duration-200
                  flex items-center gap-1
                  whitespace-nowrap
                `}
                aria-label="Next tip"
              >
                <span className="hidden sm:inline">Next tip</span>
                <span className="sm:hidden">Next</span>
                <ChevronRight size={14} className="sm:w-3.5 sm:h-3.5" />
              </button>
            ) : (
              <button
                onClick={handleGotIt}
                className={`
                  px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg
                  bg-[#8B4513] text-white
                  hover:bg-[#6B3410]
                  transition-colors duration-200
                  whitespace-nowrap
                `}
                aria-label="Got it"
              >
                Got it!
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (centerInViewport && typeof document !== 'undefined') {
    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black bg-opacity-30" aria-hidden="true" />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Pro tips"
          className="relative z-10 w-full max-w-lg max-h-full min-h-0 overflow-y-auto overscroll-contain"
        >
          {tipCard}
        </div>
      </div>,
      document.body
    );
  }

  return tipCard;
};

export default TipBoxCarousel;

